import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import { serviceLabel } from "../screens/overview/vocab.ts";

/**
 * The service switcher: a compact dropdown at the right end of the data-quality strip row that
 * jumps between the three hosted-service admin consoles (IDP, VCI, AIS). It renders ONLY on
 * `/services/idp`, `/services/vci` and `/services/ais`; everywhere else it returns null so the
 * strip looks exactly as before.
 *
 * It is a custom listbox-button (not a native `<select>`): the trigger carries `aria-haspopup=
 * "listbox"` / `aria-expanded`, and the popup is a `role="listbox"` of `role="option"` items with
 * `aria-activedescendant` driving the keyboard-active option. Keys: ArrowDown/ArrowUp/Enter/Space
 * open from the trigger; in the list ArrowDown/ArrowUp move, Home/End jump, Enter/Space select,
 * Escape closes (focus back on the trigger) and Tab closes. A mousedown outside closes.
 *
 * The popup is portalled into `document.body` and fixed-positioned under the trigger, right-
 * aligned, so the strip's overflow can never clip it; the `data-theme` tokens on `<html>` still
 * apply. Options show the service name only (plus a check on the current one): per-service status
 * isn't available here without extra requests. Selecting a different service navigates to exactly
 * `/services/<slug>` (query/hash dropped) which lands on that console's default Dashboard tab.
 */

export type SwitchableService = "idp" | "vci" | "ais";

/** The service slug for a `/services/{idp|vci|ais}` path (optional trailing slash), else null. */
export function serviceSlugFromPath(pathname: string): SwitchableService | null {
  const match = /^\/services\/(idp|vci|ais)\/?$/.exec(pathname);
  return match === null ? null : (match[1] as SwitchableService);
}

export const SERVICE_SWITCH_OPTIONS: readonly { readonly slug: SwitchableService }[] = [
  { slug: "idp" },
  { slug: "vci" },
  { slug: "ais" },
];

interface MenuPosition {
  readonly top: number;
  readonly right: number;
}

export function ServiceSwitcher(): ReactNode {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const current = serviceSlugFromPath(pathname);

  const baseId = useId();
  const listId = `${baseId}-list`;
  const optionId = (slug: SwitchableService): string => `${baseId}-opt-${slug}`;

  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [position, setPosition] = useState<MenuPosition>({ top: 0, right: 0 });
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const currentIndex = SERVICE_SWITCH_OPTIONS.findIndex((o) => o.slug === current);

  const place = useCallback((): void => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (rect === undefined) return;
    setPosition({ top: rect.bottom + 4, right: Math.max(0, window.innerWidth - rect.right) });
  }, []);

  // Anchor under the trigger as soon as it opens (before paint) and keep it anchored on resize/scroll.
  useLayoutEffect(() => {
    if (!open) return;
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open, place]);

  // Close on a mousedown outside both the trigger and the popup (listener only while open).
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent): void => {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target) === true) return;
      if (listRef.current?.contains(target) === true) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  // Move focus into the listbox when it opens (the active option is exposed via aria-activedescendant).
  useEffect(() => {
    if (open) listRef.current?.focus();
  }, [open]);

  // Leaving the service pages while open (e.g. browser back) must not leave a stale popup behind.
  useEffect(() => {
    if (current === null) setOpen(false);
  }, [current]);

  if (current === null) return null;

  const openMenu = (): void => {
    setActiveIndex(currentIndex < 0 ? 0 : currentIndex);
    setOpen(true);
  };

  const closeToTrigger = (): void => {
    setOpen(false);
    triggerRef.current?.focus();
  };

  const select = (slug: SwitchableService): void => {
    closeToTrigger();
    if (slug !== current) navigate(`/services/${slug}`);
  };

  const onTriggerKeyDown = (e: ReactKeyboardEvent<HTMLButtonElement>): void => {
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      if (!open) openMenu();
    }
    // Enter / Space fall through to the button's native click, which toggles the menu.
  };

  const onListKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>): void => {
    const last = SERVICE_SWITCH_OPTIONS.length - 1;
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setActiveIndex((i) => Math.min(last, i + 1));
        break;
      case "ArrowUp":
        e.preventDefault();
        setActiveIndex((i) => Math.max(0, i - 1));
        break;
      case "Home":
        e.preventDefault();
        setActiveIndex(0);
        break;
      case "End":
        e.preventDefault();
        setActiveIndex(last);
        break;
      case "Enter":
      case " ": {
        e.preventDefault();
        const option = SERVICE_SWITCH_OPTIONS[activeIndex];
        if (option !== undefined) select(option.slug);
        break;
      }
      case "Escape":
        e.preventDefault();
        closeToTrigger();
        break;
      case "Tab":
        // No focus trap: close and let focus move naturally.
        setOpen(false);
        break;
      default:
        break;
    }
  };

  const currentName = serviceLabel(current);
  const activeOption = SERVICE_SWITCH_OPTIONS[activeIndex];

  return (
    <div className="svc-switch">
      <button
        ref={triggerRef}
        type="button"
        className="svc-switch__trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-label={`Switch service, current: ${currentName}`}
        onClick={() => (open ? setOpen(false) : openMenu())}
        onKeyDown={onTriggerKeyDown}
      >
        <span className="svc-switch__label">{currentName}</span>
        <svg
          className="svc-switch__chevron"
          viewBox="0 0 24 24"
          width="12"
          height="12"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>
      {open &&
        createPortal(
          <div
            ref={listRef}
            id={listId}
            className="svc-switch__menu"
            role="listbox"
            aria-label="Service"
            tabIndex={-1}
            aria-activedescendant={activeOption === undefined ? undefined : optionId(activeOption.slug)}
            style={{ top: position.top, right: position.right }}
            onKeyDown={onListKeyDown}
          >
            {SERVICE_SWITCH_OPTIONS.map((o, idx) => {
              const selected = o.slug === current;
              return (
                <div
                  key={o.slug}
                  id={optionId(o.slug)}
                  role="option"
                  aria-selected={selected}
                  className="svc-switch__option"
                  data-active={idx === activeIndex ? "true" : undefined}
                  data-selected={selected ? "true" : undefined}
                  onMouseEnter={() => setActiveIndex(idx)}
                  onClick={() => select(o.slug)}
                >
                  <span className="svc-switch__check" aria-hidden="true">
                    {selected && (
                      <svg
                        viewBox="0 0 24 24"
                        width="14"
                        height="14"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M5 12l5 5 9-10" />
                      </svg>
                    )}
                  </span>
                  <span>{serviceLabel(o.slug)}</span>
                </div>
              );
            })}
          </div>,
          document.body,
        )}
    </div>
  );
}
